package com.milta.feature.aicredit.data.remote

import com.milta.core.network.Endpoints
import com.milta.core.network.apiExceptionOf
import com.milta.core.network.baseUrl
import com.milta.feature.aicredit.data.remote.dto.AdminAccountAiCreditDto
import com.milta.feature.aicredit.data.remote.dto.AdminAiCreditDto
import com.milta.feature.aicredit.data.remote.dto.AdminAiCreditHistoryItemDto
import com.milta.feature.aicredit.data.remote.dto.AiCreditDto
import com.milta.feature.aicredit.data.remote.dto.AiCreditHistoryItemDto
import com.milta.feature.aicredit.data.remote.dto.AiCreditSettingsDto
import com.milta.feature.aicredit.data.remote.dto.ReplaceRemainingAiCreditRequest
import com.milta.feature.auth.data.remote.dto.ApiError
import com.milta.feature.route.data.remote.dto.PagedResultDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess

/** MIL-98 — AI credit per account: the caller's own (mobile + web) and the Admin views. */
class AiCreditApi(private val client: HttpClient) {

    /** GET /api/v1/ai-credit/me */
    suspend fun getMyCredit(): AiCreditDto =
        client.get("$baseUrl/${Endpoints.aiCreditMe}").decodeOrThrow()

    /** GET /api/v1/ai-credit/me/history — most recent first. */
    suspend fun getMyHistory(page: Int, pageSize: Int): PagedResultDto<AiCreditHistoryItemDto> =
        client.get("$baseUrl/${Endpoints.aiCreditMeHistory}") {
            parameter("page", page)
            parameter("pageSize", pageSize)
        }.decodeOrThrow()

    /** GET /api/v1/admin/usage-limits/ai */
    suspend fun getSettings(): AiCreditSettingsDto =
        client.get("$baseUrl/${Endpoints.adminAiCreditSettings}").decodeOrThrow()

    /** PUT /api/v1/admin/usage-limits/ai — each invalid field is its own 400 `error.validation.*`. */
    suspend fun updateSettings(settings: AiCreditSettingsDto): AiCreditSettingsDto =
        client.put("$baseUrl/${Endpoints.adminAiCreditSettings}") {
            contentType(ContentType.Application.Json)
            setBody(settings)
        }.decodeOrThrow()

    /** GET /api/v1/admin/accounts/{accountId}/ai-credit */
    suspend fun getAccountCredit(accountId: String): AdminAccountAiCreditDto =
        client.get("$baseUrl/${Endpoints.adminAccountAiCredit(accountId)}").decodeOrThrow()

    /** GET /api/v1/admin/accounts/{accountId}/ai-credit/history */
    suspend fun getAccountHistory(accountId: String, page: Int, pageSize: Int): PagedResultDto<AdminAiCreditHistoryItemDto> =
        client.get("$baseUrl/${Endpoints.adminAccountAiCreditHistory(accountId)}") {
            parameter("page", page)
            parameter("pageSize", pageSize)
        }.decodeOrThrow()

    /** PUT /api/v1/admin/accounts/{accountId}/ai-credit/remaining — returns the updated credit. */
    suspend fun replaceRemaining(accountId: String, remainingEur: Double): AdminAiCreditDto =
        client.put("$baseUrl/${Endpoints.adminAccountAiCreditRemaining(accountId)}") {
            contentType(ContentType.Application.Json)
            setBody(ReplaceRemainingAiCreditRequest(remainingEur))
        }.decodeOrThrow()

    private suspend inline fun <reified T> HttpResponse.decodeOrThrow(): T {
        if (!status.isSuccess()) {
            val apiError = runCatching { body<ApiError>() }.getOrNull()
            throw apiExceptionOf(status.value, apiError)
        }
        return body()
    }
}
